// client/src/utils/effectFormatter.js

/**
 * Traduit un objet d'effet structuré en description lisible par un joueur.
 * @param {Object|String} effect - L'effet à décrire
 * @returns {String} - Description humaine
 */
export const formatEffect = (effect) => {
  if (!effect) return '';
  if (typeof effect === 'string') return effect;

  const parts = [];

  // Trigger (quand l'effet se déclenche)
  if (effect.trigger) {
    parts.push(formatTrigger(effect.trigger));
  }

  // Type d'effet
  const desc = formatEffectType(effect);
  if (desc) parts.push(desc);

  // Condition
  if (effect.condition) {
    parts.push(formatCondition(effect.condition));
  }

  return parts.filter(Boolean).join('. ');
};

const formatTrigger = (trigger) => {
  const triggers = {
    'ON_SUMMON': 'À l\'invocation',
    'ON_DEATH': 'À la destruction',
    'ON_ATTACK': 'À l\'attaque',
    'ON_DEFEND': 'À la défense',
    'ON_TURN_START': 'Au début du tour',
    'ON_TURN_END': 'À la fin du tour',
    'ON_SPELL_CAST': 'Lorsqu\'un sort est lancé',
    'ON_PLAY': 'À l\'activation',
    'CONTINUOUS': 'Effet continu',
    'PASSIVE': 'Effet passif',
    'ACTIVE': 'Capacité activable',
  };
  return triggers[trigger] || trigger;
};

const formatEffectType = (effect) => {
  const { type, value, target, duration } = effect;
  if (!type) return effect.description || '';

  const targetText = formatTarget(target);
  const valueText = value !== undefined ? value : '';

  switch (type) {
    case 'DAMAGE':
      return `Inflige ${valueText} dégât(s) ${targetText}`;
    case 'HEAL':
    case 'RESTORE_HP':
      return `Soigne ${valueText} point(s) de vie ${targetText}`;
    case 'DRAW':
      return `Le joueur pioche ${valueText} carte(s)`;
    case 'DISCARD':
      return `L'adversaire défausse ${valueText} carte(s)`;
    case 'DESTROY':
      return `Détruit une carte ${targetText}`;
    case 'REVIVE':
      return `Ressuscite une carte ${targetText}`;
    case 'BOOST_ATK':
      return `+${valueText} ATK ${targetText}${duration ? ` pendant ${duration} tour(s)` : ''}`;
    case 'BOOST_DEF':
      return `+${valueText} PV ${targetText}${duration ? ` pendant ${duration} tour(s)` : ''}`;
    case 'REDUCE_ATK':
      return `-${valueText} ATK ${targetText}`;
    case 'REDUCE_DEF':
      return `-${valueText} PV ${targetText}`;
    case 'FREEZE':
      return `Gèle ${targetText} (ne peut pas attaquer au prochain tour)`;
    case 'SUMMON_TOKEN':
      return `Invoque ${valueText || 1} jeton(s) ${targetText}`;
    case 'SHIELD':
      return `Confère un bouclier ${targetText} (absorbe la prochaine attaque)`;
    case 'ENERGY_GAIN':
      return `+${valueText} énergie ${targetText}`;
    case 'CRYSTAL_GAIN':
      return `+${valueText} cristal(aux) ${targetText}`;
    case 'REVEAL_TOP':
      return `Révèle la carte du dessus du deck`;
    case 'STEAL':
      return `Vole une carte ${targetText}`;
    case 'SWAP_POSITION':
      return `Échange la position de deux créatures`;
    case 'RETURN_TO_HAND':
      return `Renvoie une carte ${targetText} dans la main`;
    case 'RETURN_TO_DECK':
      return `Renvoie une carte ${targetText} dans le deck`;
    case 'BUFF_ELEMENT':
      return `+${valueText || 1} ATK/PV aux créatures ${valueText ? '' : 'du même élément'}`;
    case 'DEBUFF_ELEMENT':
      return `-${valueText || 1} ATK/PV aux créatures adverses du même élément`;
    case 'LIFE_GAIN':
      return `+${valueText} PV ${targetText}`;
    case 'MANA_GAIN':
      return `+${valueText} mana ${targetText}`;
    case 'TRANSFORM':
      return `Transforme la carte ${targetText}`;
    case 'COPY':
      return `Copie les capacités ${targetText}`;
    case 'MULTI_ATTACK':
      return `Peut attaquer ${valueText || 2} fois par tour`;
    case 'PREVENT_ATTACK':
      return `L'adversaire ne peut pas attaquer ce tour`;
    case 'COUNTER':
      return `Annule le dernier sort joué`;
    case 'PIERCING':
      return `Les dégâts de combat traversent les PV (protection)`;
    case 'CONTINUOUS':
    case 'PASSIVE':
    case 'ACTIVE':
    case 'SPECIAL':
      if (effect.description) return effect.description;
      return `${formatTrigger(type)}`;
    case 'TRIGGER':
      return effect.description || `Effet déclenché`;
    default:
      // Fallback: si l'effet a une description textuelle, l'utiliser
      if (effect.description) return effect.description;
      // Sinon construire à partir des propriétés disponibles
      return `${type}${targetText ? ' → ' + targetText : ''}${valueText ? ' (' + valueText + ')' : ''}`;
  }
};

const formatTarget = (target) => {
  if (!target) return '';
  const targets = {
    'OPPONENT': 'à l\'adversaire',
    'OPPONENT_PLAYER': 'à l\'adversaire',
    'SELF': 'au joueur',
    'SELF_PLAYER': 'au joueur',
    'ALL_OPPONENT': 'à toutes les créatures adverses',
    'ALL_SELF': 'à toutes ses créatures',
    'ALL': 'à toutes les créatures',
    'OPPONENT_CREATURE': 'à une créature adverse',
    'SELF_CREATURE': 'à une créature alliée',
    'ANY_CREATURE': 'à une créature',
    'ALLY_ELEMENT': 'aux créatures alliées du même élément',
    'RANDOM': 'à une cible aléatoire',
    'NONE': '',
  };
  return targets[target] || target;
};

const formatCondition = (condition) => {
  if (!condition) return '';
  if (typeof condition === 'string') {
    const conditions = {
      'IF_FIRE': 'Uniquement si vous contrôlez un élément Feu',
      'IF_WATER': 'Uniquement si vous contrôlez un élément Eau',
      'IF_EARTH': 'Uniquement si vous contrôlez un élément Terre',
      'IF_DARK': 'Uniquement si vous contrôlez un élément Ténèbres',
      'IF_LIGHT': 'Uniquement si vous contrôlez un élément Lumière',
      'IF_RAINBOW': 'Uniquement si vous contrôlez un élément Arc-en-ciel',
      'IF_NO_CARDS_IN_HAND': 'Uniquement si vous n\'avez pas de cartes en main',
      'IF_DISCIPLINE_5': 'Uniquement si vous avez 5 cartes ou plus dans votre main',
    };
    return conditions[condition] || condition;
  }
  if (typeof condition === 'object') {
    return condition.description || JSON.stringify(condition);
  }
  return '';
};

/**
 * Formate une liste d'effets en une description complète.
 * @param {Array} effects - Tableau d'effets
 * @returns {String} - Description formatée
 */
export const formatEffectList = (effects) => {
  if (!effects || !Array.isArray(effects) || effects.length === 0) return '';
  return effects.map(formatEffect).filter(Boolean).join('. ');
};

/**
 * Génère une description complète pour une carte.
 * Utilise card.effect (texte) si disponible, sinon construit depuis effects[].
 * @param {Object} card - La carte
 * @returns {String} - Description lisible
 */
export const getCardEffectDescription = (card) => {
  // Si la carte a déjà un texte descriptif, l'utiliser
  if (card.effect && typeof card.effect === 'string' && card.effect.trim()) {
    return card.effect;
  }
  if (card.description && typeof card.description === 'string' && card.description.trim()) {
    return card.description;
  }
  // Sinon, formater les effets structurés
  if (card.effects && Array.isArray(card.effects) && card.effects.length > 0) {
    return formatEffectList(card.effects);
  }
  // Carte sans effet
  if (card.abilities && Array.isArray(card.abilities) && card.abilities.length > 0) {
    return card.abilities.map(a => {
      if (typeof a === 'string') return formatAbilityName(a);
      return a.name || formatAbilityName(a.type) || '';
    }).filter(Boolean).join(', ');
  }
  return 'Aucun effet';
};

const formatAbilityName = (ability) => {
  const abilities = {
    'FIRST_STRIKE': 'Frappe préventive',
    'DOUBLE_STRIKE': 'Double frappe',
    'VAMPIRISM': 'Vampirisme',
    'ECHO': 'Écho',
    'PROVOKE': 'Provocation',
    'STEALTH': 'Discrétion',
    'FLIGHT': 'Vol',
    'GHOST_STRIKE': 'Frappe fantôme',
    'LETHAL': 'Létal',
    'FREEZE': 'Gel',
    'RADIANT_AURA': 'Aura rayonnante',
    'DIVINE_AURA': 'Aura divine',
    'SYMBIOSIS': 'Symbiose',
    'PIERCING': 'Perforation',
    'COUNTER_STRIKE': 'Riposte',
    'GUARDIAN_VEIL': 'Voile gardien',
    'SHIELD': 'Bouclier',
    'ENRAGE': 'Enragé',
    'IMMUTABLE': 'Immuable',
    'RESONANCE': 'Résonance',
    'HERITAGE': 'Héritage',
    'CHARGE': 'Charge',
    'REGENERATION': 'Régénération',
    'TRANSCENDENCE': 'Transcendance',
  };
  return abilities[ability] || ability;
};

// R56 — descriptions joueur des keywords (sémantiques vérifiées moteur,
// PROVOKE corrigée : taunt OFFENSIF — décision Julian 02:58).
export const KEYWORD_DESCRIPTIONS = {
  FIRST_STRIKE: 'Frappe en premier dans les combats : les dégâts sont infligés avant la riposte.',
  DOUBLE_STRIKE: 'Double frappe : attaque deux fois par tour de combat.',
  VAMPIRISM: 'Vampirisme : régénère des PV équivalents aux dégâts infligés.',
  ECHO: 'Écho : si elle détruit un défenseur, les dégâts excédentaires frappent le joueur adverse.',
  PROVOKE: 'Provocation : quand ce monstre ATTAQUE, l’adversaire doit placer un défenseur devant lui AVANT de défendre tout autre attaquant.',
  STEALTH: 'Discrétion : ne peut être bloqué que par un autre monstre discret (sinon attaque directe).',
  FLIGHT: 'Vol : ne peut être bloqué que par un autre monstre volant (sinon attaque directe).',
  GHOST_STRIKE: 'Frappe fantôme : attaque deux fois par tour de combat.',
  LETHAL: 'Létal : toute créature ayant subi ses dégâts est détruite.',
  FREEZE: 'Gel : le défenseur touché ne peut pas attaquer au tour suivant.',
  RADIANT_AURA: 'Aura rayonnante : inflige aussi ses dégâts aux défenseurs adjacents.',
  DIVINE_AURA: 'Aura divine : inflige aussi ses dégâts à TOUS les défenseurs.',
  SYMBIOSIS: 'Symbiose : +1 ATK par autre allié du même élément sur le terrain.',
  PIERCING: 'Perforation : les dégâts excédentaires traversent le défenseur.',
  COUNTER_STRIKE: 'Riposte : en défense, frappe en premier comme une Frappe préventive.',
  GUARDIAN_VEIL: 'Voile gardien : réduit de 1 tous les dégâts subis.',
  SHIELD: 'Bouclier : absorbe entièrement le premier coup reçu.',
  ENRAGE: 'Enragé : +1 ATK permanent à chaque dégât subi.',
  IMMUTABLE: 'Immuable : ne peut être détruit par les effets ni forcé en défense (les dégâts de combat restent fatals).',
  RESONANCE: 'Résonance : ignore les désavantages élémentaires.',
  HERITAGE: 'Héritage : à sa mort, lègue ses capacités à vos autres monstres.',
  CHARGE: 'Charge : attaque dès son arrivée (sans mal d’invocation).',
  REGENERATION: 'Régénération : récupère des PV à chaque tour.',
  TRANSCENDENCE: 'Transcendance : ignore Voile gardien, Boucliers et la priorité de blocage de la Provocation.',
};

export const abilityDescription = (ability) =>
  KEYWORD_DESCRIPTIONS[ability] || null;

export { formatAbilityName };
